/*
 * Parla Oral Care — Free Gift: Paste Press Toothpaste Key
 *
 * Adds the Paste Press Key (free) when a qualifying 3-pack / Discovery Kit is
 * ADDED to the cart. It adds the £0 "FREE GIFT" clone of the Key — the same
 * product the bundle builder uses — so it needs no discount and never competes
 * with other codes. (It used to add the paid Key and rely on the automatic
 * "FreePastePressKey" Buy X get Y discount to zero it; that discount is retired.)
 *
 * Qualifying is matched on product_id, so it covers one-time AND subscription
 * purchases of the same product — a selling plan changes the variant and price,
 * never the product_id.
 *
 * ---------------------------------------------------------------------------
 * DESIGN RULES — both of these come from real customer complaints, please keep
 * them in mind before "improving" the trigger logic:
 *
 * 1. THE GIFT IS OFFERED ONCE PER ADD, NEVER RE-ADDED.
 *    We only ADD after a cart add whose own response says a qualifying product
 *    went in. We deliberately never add on /cart/change, /cart/update,
 *    /cart/clear, or on page load. That is what makes a customer's removal of
 *    the Key stick: previously any cart write (and every page load) re-ran a
 *    "cart contains qualifier but no Key → add it" check, so the Key kept
 *    reappearing the moment they deleted it, and adding an unrelated item or
 *    reloading brought it straight back.
 *
 * 2. THE GIFT LEAVES WITH THE LAST QUALIFYING PRODUCT.
 *    It is a £0 product with no discount behind it, so a Key left in a cart
 *    with no 3-pack would ship free on its own. After every /cart/change,
 *    /cart/update and /cart/clear, and once on page load, any gift line in a
 *    cart with no qualifying product is removed. Removal only — these paths
 *    never add (rule 1).
 *
 * 3. THE BUNDLE BUILDER IS A SEPARATE SYSTEM — STAY OUT OF IT.
 *    /pages/build-your-routine (bk-bundle-builder) builds the cart itself and
 *    adds the same £0 gift product. Its bulk /cart/add contains qualifying
 *    3-packs, so without the bail-out below this script would add a second
 *    gift on top of the funnel's. This script owns the normal storefront (PDP,
 *    cart drawer upsell); the funnel owns itself.
 * ---------------------------------------------------------------------------
 *
 * URL MATCHING: the theme posts to `routes.cart_add_url`, which Liquid renders
 * as `/cart/add` with NO `.js` suffix (and a locale prefix on non-primary
 * markets, e.g. `/en-gb/cart/add`). Skio's plan picker posts to `/cart/add.js`.
 * The matcher has to cover all of those shapes.
 */

(function () {
  'use strict';

  /*
   * DESIGN RULE 3 — do nothing at all on a page carrying the bundle builder.
   * #bkbb is that section's root element (see bk-bundle-builder.liquid). We are
   * loaded with `defer` at the end of <body>, so the DOM is parsed by now and
   * this check is reliable; keep the defer if this include is ever moved.
   * Bailing before the fetch patch leaves the funnel completely untouched.
   */
  if (document.getElementById('bkbb')) return;

  // The £0 gift clone, not the paid Key — see the header. The cart locks its
  // quantity via snippets/bk-gift-only.liquid.
  const FREE_GIFT_VARIANT_ID = 56393318564219; // PASTE PRESS TOOTHPASTE KEY - FREE GIFT — Default Title
  const FREE_GIFT_PRODUCT_ID = 15428419813755; // PASTE PRESS TOOTHPASTE KEY - FREE GIFT
  const FREE_GIFT_PROP = '_is_free_gift';

  const QUALIFYING_PRODUCT_IDS = [
    15161007964539, // Toothpaste Discovery Kit
    15158916710779, // Bio Balance Toothpaste x3
    15158915694971, // Enamel Protect Toothpaste x3
    15157001257339, // Pro Whitening Toothpaste x3
  ];

  // Captured before we patch window.fetch. Every request we make ourselves goes
  // through this, so our own cart writes can never re-trigger the interceptor.
  const nativeFetch = window.fetch.bind(window);

  // Adds only — see DESIGN RULE 1. Matches /cart/add, /cart/add.js,
  // /en-gb/cart/add, and any of those with a query string.
  const CART_ADD_RE = /\/cart\/add(?:\.js)?(?:[?#]|$)/;
  // Writes that can remove a qualifying product — DESIGN RULE 2.
  const CART_REMOVE_RE = /\/cart\/(?:change|update|clear)(?:\.js)?(?:[?#]|$)/;

  let checking = false;
  let recheckQueued = false;

  function numberOr(value, fallback) {
    return typeof value === 'number' ? value : fallback;
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    if (input && typeof input.href === 'string') return input.href;
    return '';
  }

  function isQualifier(productId) {
    return QUALIFYING_PRODUCT_IDS.includes(productId);
  }

  /*
   * Pull the product ids out of a /cart/add response. The theme's product form
   * gets back a single line-item object (Dawn reads `parsedState.id` from it);
   * bulk adds come back as { items: [...] }. Handle both, and if the shape is
   * something we don't recognise return nothing — failing to add the gift is a
   * visible, fixable problem, whereas guessing risks re-adding a Key the
   * customer just deleted, which is the bug we are fixing.
   */
  function addedProductIds(data) {
    if (!data) return [];
    const list = Array.isArray(data.items) ? data.items : [data];
    return list.map((item) => item && item.product_id).filter((id) => typeof id === 'number');
  }

  /* ------------------------------------------------------------------ *
   * Section rendering
   *
   * The gift goes in on a second request, after the theme has already
   * painted the drawer from its own add-to-cart response — so that paint
   * does not contain the gift. We therefore ask for the cart sections on
   * our own write and repaint them, so the Key shows up immediately
   * instead of on the next page load.
   *
   * Both cart UIs are honoured, each with its own contract:
   *   cart-drawer  → response keyed by section.id
   *   cart-items   → response keyed by section.section  (cart page only)
   *
   * `cart-icon-bubble` is deliberately skipped: this theme's bk-header renders
   * its own cart icon and [data-bk-cart-count] badge, so painting the stock
   * section into #cart-icon-bubble would replace the brand markup with Dawn's.
   * The count is updated in place by updateHeaderCount() instead.
   * ------------------------------------------------------------------ */
  const SKIP_SECTIONS = ['cart-icon-bubble'];

  function renderPlan() {
    const plan = [];

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        drawer.getSectionsToRender().forEach((section) => {
          if (SKIP_SECTIONS.includes(section.id)) return;
          plan.push({
            key: section.id,
            selector: section.selector,
            host: drawer,
            resolve: () =>
              section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id),
          });
        });
      } catch (err) {
        console.error('[Parla] free gift: cart drawer sections unavailable', err);
      }
    }

    // <cart-items> is the cart page only; <cart-drawer-items> is a different tag.
    const items = document.querySelector('cart-items');
    if (items && typeof items.getSectionsToRender === 'function') {
      try {
        items.getSectionsToRender().forEach((section) => {
          if (SKIP_SECTIONS.includes(section.section)) return;
          plan.push({
            key: section.section,
            selector: section.selector,
            host: items,
            resolve: () => {
              const container = document.getElementById(section.id);
              if (!container) return null;
              return container.querySelector(section.selector) || container;
            },
          });
        });
      } catch (err) {
        console.error('[Parla] free gift: cart page sections unavailable', err);
      }
    }

    return plan;
  }

  function applySections(sections) {
    if (!sections) return;

    renderPlan().forEach((entry) => {
      const html = sections[entry.key];
      if (!html) return;
      const target = entry.resolve();
      if (!target) return;
      try {
        target.innerHTML = entry.host.getSectionInnerHTML(html, entry.selector);
      } catch (err) {
        console.error('[Parla] free gift: could not render section', entry.key, err);
      }
    });

    rebindDrawerOverlay();
  }

  // #CartDrawer-Overlay lives *inside* #CartDrawer, so repainting that markup
  // drops the click-to-close listener bound in the drawer's constructor. Dawn
  // re-binds it in renderContents(); we have to do the same after our repaint,
  // or clicking outside the drawer stops closing it.
  function rebindDrawerOverlay() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
  }

  // These classes sit on elements outside the repainted markup. Removing the
  // gift can leave the cart empty, so this has to toggle both ways.
  function syncEmptyState(itemCount) {
    const empty = itemCount === 0;

    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', empty);

    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);

    const items = document.querySelector('cart-items');
    if (items) items.classList.toggle('is-empty', empty);
  }

  // See SKIP_SECTIONS: the header count is patched in place rather than by
  // repainting the stock cart-icon-bubble section over the brand markup.
  function updateHeaderCount(itemCount) {
    if (typeof itemCount !== 'number') return;

    document.querySelectorAll('[data-bk-cart-count]').forEach((el) => {
      el.textContent = itemCount;
      if (itemCount === 0) {
        el.setAttribute('hidden', '');
      } else {
        el.removeAttribute('hidden');
      }
    });

    // Dawn-style header, in case the theme ever falls back to one.
    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
      el.textContent = itemCount;
    });
  }

  function withSections(body) {
    const keys = renderPlan()
      .map((entry) => entry.key)
      .filter(Boolean);
    if (keys.length) {
      body.sections = Array.from(new Set(keys)).join(',');
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  async function addGiftToCart() {
    const body = withSections({
      id: FREE_GIFT_VARIANT_ID,
      quantity: 1,
      properties: { [FREE_GIFT_PROP]: 'true' },
    });

    const res = await nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const detail = (data && (data.description || data.message)) || '';
      throw new Error('/cart/add.js → ' + res.status + ' ' + detail);
    }
    return data;
  }

  /*
   * Called ONLY after a qualifying product was just added (see DESIGN RULE 1).
   * Adds the Key unless one is already in the cart.
   */
  async function offerFreeGift() {
    if (checking) {
      recheckQueued = true;
      return;
    }
    checking = true;

    try {
      const res = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const items = (cart && cart.items) || [];

      const hasQualifier = items.some((item) => isQualifier(item.product_id));
      const hasKey = items.some((item) => item.product_id === FREE_GIFT_PRODUCT_ID);

      // hasQualifier is re-checked here because the add we reacted to could have
      // been rolled back (sold out, quantity rules) between the two requests.
      if (!hasQualifier || hasKey) return;

      const result = await addGiftToCart();
      const nextCount = numberOr(result && result.item_count, (cart.item_count || 0) + 1);

      syncEmptyState(nextCount);
      updateHeaderCount(nextCount);
      applySections(result && result.sections);

      document.dispatchEvent(
        new CustomEvent('parla:cart:updated', { bubbles: true, detail: { cart: result } })
      );
    } catch (err) {
      console.error('[Parla] free gift add failed:', err);
    } finally {
      checking = false;
      if (recheckQueued) {
        recheckQueued = false;
        offerFreeGift();
      }
    }
  }

  /*
   * DESIGN RULE 2 — the gift never outlives the qualifying products. Run after
   * any cart write that could have removed one, and once on page load to catch
   * changes made elsewhere (another tab, checkout). Removes every gift line.
   */
  let pruning = false;
  let repruneQueued = false;

  async function removeOrphanedGift() {
    if (pruning) {
      repruneQueued = true;
      return;
    }
    pruning = true;

    try {
      const res = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const items = (cart && cart.items) || [];

      const giftLines = items.filter((item) => item.product_id === FREE_GIFT_PRODUCT_ID);
      if (!giftLines.length || items.some((item) => isQualifier(item.product_id))) return;

      const updates = {};
      giftLines.forEach((item) => {
        updates[item.key] = 0;
      });

      const updateRes = await nativeFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(withSections({ updates })),
      });
      const result = await updateRes.json().catch(() => null);
      if (!updateRes.ok) {
        const detail = (result && (result.description || result.message)) || '';
        throw new Error('/cart/update.js → ' + updateRes.status + ' ' + detail);
      }

      const nextCount = numberOr(result && result.item_count, 0);
      syncEmptyState(nextCount);
      updateHeaderCount(nextCount);
      applySections(result && result.sections);

      document.dispatchEvent(
        new CustomEvent('parla:cart:updated', { bubbles: true, detail: { cart: result } })
      );
    } catch (err) {
      console.error('[Parla] free gift removal failed:', err);
    } finally {
      pruning = false;
      if (repruneQueued) {
        repruneQueued = false;
        removeOrphanedGift();
      }
    }
  }

  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    const url = urlOf(input);

    // Any write that can drop a line — never adds the gift (DESIGN RULE 1).
    if (CART_REMOVE_RE.test(url)) {
      promise.then((response) => {
        // Fresh task, so the theme paints its own update first.
        if (response && response.ok) setTimeout(removeOrphanedGift, 0);
      }, () => {});
      return promise;
    }

    if (!CART_ADD_RE.test(url)) return promise;

    return promise.then((response) => {
      if (!response || !response.ok) return response;

      // Read a CLONE — the caller still has to consume the original body.
      // Only a qualifying product going in may trigger the gift; adding
      // anything else must never resurrect a Key the customer deleted.
      response
        .clone()
        .json()
        .then((data) => {
          if (addedProductIds(data).some(isQualifier)) {
            // Fresh task, so the theme paints its own update first.
            setTimeout(offerFreeGift, 0);
          }
        })
        .catch(() => {
          /* non-JSON add response — nothing we can safely act on */
        });

      return response;
    });
  };

  // Manual hook for debugging from the console. Note this bypasses DESIGN RULE 1
  // (it does not require a qualifying add), so it can re-add a Key the customer
  // removed — for testing only, never wire it to a UI event.
  window.parlaFreeGiftOffer = offerFreeGift;

  // Catch a cart whose 3-pack was removed outside this page (DESIGN RULE 2).
  removeOrphanedGift();
})();
